import {
    Sheet,
    SheetContent,
} from "@/components/ui/sheet";
import React, { FC, useEffect, useRef, useState } from "react";
import { UserIcon, PhoneIcon, MailIcon, CheckCircle2Icon, LoaderIcon, XCircleIcon, Calendar, Clock, } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
    GoogleMap,
} from '@react-google-maps/api';
import { seperateDateAndTime } from "@/utils/formChange";
import { getCurrencySymbol } from "@/lib/utils";
import { updateReservation } from "../services/reservation-service";
import { CalendarEvent } from "../calendar/calendar-types";
import { useGoogleMaps } from "@/context/GoogleMapsContext";
import { useUserContext } from "@/context/UserContext";
import { grayMapStyle } from "@/utils/variables";

interface Props {
    selectedOrder: CalendarEvent;
    isSheetOpen: boolean;
    setIsSheetOpen: (isOpen: boolean) => void;
    onSave?: (updatedEvent: CalendarEvent) => void;
}

const OrderDetails: FC<Props> = ({ selectedOrder, isSheetOpen, setIsSheetOpen, onSave }) => {
    const data = selectedOrder;
    const { decodedToken } = useUserContext();
    const { isLoaded, loadError } = useGoogleMaps();

    const [openDialog, setOpenDialog] = React.useState(false);
    const [status, setStatus] = React.useState(selectedOrder?.status);

    const [isUserPartner, setIsUserPartner] = useState<boolean>(false);
    const [currency, setCurrency] = useState<string>('');

    const mapRef = useRef<google.maps.Map | null>(null);
    const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);

    const onMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
        directionsRendererRef.current = new google.maps.DirectionsRenderer({
            map: map,
            polylineOptions: {
                strokeColor: '#0000FF',
                strokeOpacity: 0.8,
                strokeWeight: 5,
            },
        });
    };

    const calculateRoute = async () => {

        if (!data?.pickup_latitude || !data?.pickup_longitude || !data?.dropoff_latitude || !data?.dropoff_longitude) {
            console.warn('Missing coordinates for route calculation');
            return;
        }

        try {
            const directionsService = new window.google.maps.DirectionsService();
            const result = await directionsService.route({
                origin: { lat: data.pickup_latitude, lng: data.pickup_longitude },
                destination: { lat: data.dropoff_latitude, lng: data.dropoff_longitude },
                travelMode: google.maps.TravelMode.DRIVING,
            });

            if (directionsRendererRef.current) {
                directionsRendererRef.current.setDirections(result);
            }

        } catch (error) {
            console.error('Error calculating route:', error);
            toast('Failed to calculate route. Please try again.', {
                className: "absolute bottom-10 right-10 z-50 text-red-500",
            });
        }
    };

    const handleConfirm = (value: string) => {
        setStatus(value);
        setOpenDialog(true);
    };

    const submit = async () => {
        try {
            if (isUserPartner) {
                return;
            }

            const updatedEvent = {...selectedOrder, status: status};

            await updateReservation(updatedEvent);
            if (onSave) {
                onSave(updatedEvent);
            }

            toast(`Order ${status === "approved" ? "approved" : "rejected"} successfully`, {
                className: "absolute bottom-10 right-10 z-50",
            });

            setOpenDialog(false);
        } catch (err: any) {
            console.error("Failed to update order status", err);
            toast(err?.response?.data?.message, {
                className: "absolute bottom-10 right-10 z-50 text-red-500",
            });
        }
    };

    useEffect(() => {
        if (data && isLoaded) {
            calculateRoute();
        }

        setStatus(data?.status);

        const currency = getCurrencySymbol(decodedToken?.permissions?.currency || 'EUR');
        setCurrency(currency);
        setIsUserPartner(decodedToken?.permissions?.user_role === 'commission_partner');
    }, [data, isLoaded, isSheetOpen]);

    if (loadError) return <p>Error loading Google Maps: {loadError.message}</p>;
    if (!isLoaded) return <p>Loading map...</p>;

    return (
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetContent side="bottom" className="h-[85vh] sm:h-[75vh] p-4 sm:p-5 overflow-y-auto">
                {/* <h2 className="text-[30px]">{data?.dropoff}</h2> */}
                <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6 lg:gap-8 h-full">
                    <div className="!rounded-xl w-full h-[4x 00px] lg:h-full">
                        {window.google &&
                            <GoogleMap

                                zoom={10}
                                center={
                                    data?.pickup_latitude && data?.pickup_longitude
                                        ? { lat: data.pickup_latitude, lng: data.pickup_longitude }
                                        : { lat: 41.3275, lng: 19.8189 }
                                }
                                mapContainerClassName="w-full h-full rounded-xl"
                                onLoad={onMapLoad}
                                options={{
                                    styles: grayMapStyle,
                                    disableDefaultUI: true,
                                    zoomControl: true,
                                    gestureHandling: "greedy"
                                }}
                            />
                        }
                    </div>
                    <div>
                        <div>
                            <div>
                                <p className="text-lg sm:text-4xl font-bold">Order details</p>
                                <p className="text-lg sm:text-2xl">{data?.dropoff}</p>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-5">
                            <Badge
                                variant="outline"
                                className="flex gap-1 px-1.5 text-muted-foreground [&_svg]:size-3"
                            >
                                {status === "approved" ? (
                                    <CheckCircle2Icon className="text-green-500 dark:text-green-400" />
                                ) : status === "rejected" ? (
                                    <XCircleIcon className="text-red-500 dark:text-red-400" />
                                ) : status === "pending" ? (
                                    <CheckCircle2Icon className="text-gray-500 dark:text-gray-400" />
                                ) : (
                                    <LoaderIcon className="text-gray-500 dark:text-gray-400" />
                                )}
                                {status === "pending"
                                    ? "Pending"
                                    : status === "approved"
                                        ? "Approved"
                                        : status === "rejected"
                                            ? "Rejected"
                                            : "Unknown"
                                }
                            </Badge>
                        </div>
                        {!isUserPartner && (
                            <div className="mt-5">
                                <h2 className="text-lg font-semibold mb-3">Order Actions</h2>
                                <div className="flex">
                                    <div className="mr-2">
                                        <Select onValueChange={handleConfirm}>
                                            <SelectTrigger className="h-8 w-40">
                                                <SelectValue placeholder="Confirm Order" />
                                            </SelectTrigger>
                                            <SelectContent align="end">
                                                <SelectItem value="approved">Approve</SelectItem>
                                                <SelectItem value="rejected">Reject</SelectItem>
                                                <SelectItem value="pending">Cancel</SelectItem>
                                            </SelectContent>
                                        </Select>

                                        <AlertDialog open={openDialog} onOpenChange={setOpenDialog}>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Confirm</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        Do you want to {status === "approved" ? "approve" : status === "pending" ? "cancel" : "reject"} this order?
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel>No</AlertDialogCancel>
                                                    <AlertDialogAction onClick={submit}>Yes</AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mt-7">
                            <h2 className="text-lg font-semibold mb-4 mt-5">Order Info</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                <div className="bg-muted rounded-xl p-4 text-center">
                                    <Calendar className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                                    <p className="text-[12px] text-muted-foreground mb-1">Pickup date</p>
                                    <p className="text-[18px] font-medium">{seperateDateAndTime(data?.start).datePart || "-"}</p>
                                </div>
                                <div className="bg-muted rounded-xl p-4 text-center">
                                    <Clock className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                                    <p className="text-[12px] text-muted-foreground mb-1">Pickup time</p>
                                    <p className="text-[18px] font-medium">{seperateDateAndTime(data?.end).timePart || "-"}</p>
                                </div>
                                <div className="bg-muted rounded-xl p-4 text-center">
                                    <span className="w-5 h-5 mx-auto text-muted-foreground mb-2" >{currency}</span>
                                    <p className="text-[12px] text-muted-foreground mb-1">Total price</p>
                                    <p className="text-[18px] font-medium">{data?.price || "-"}</p>
                                </div>
                                <div className="bg-muted rounded-xl p-4 text-center">
                                    <UserIcon className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                                    <p className="text-[12px] text-muted-foreground mb-1">Client Name</p>
                                    <p className="text-[18px] font-medium">{data?.customerName || "-"}</p>
                                </div>
                                <div className="bg-muted rounded-xl p-4 text-center">
                                    <PhoneIcon className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                                    <p className="text-[12px] text-muted-foreground mb-1">Client Phone</p>
                                    <p className="text-[18px] font-medium">{data?.customerPhone || "-"}</p>
                                </div>
                                <div className="bg-muted rounded-xl p-4 text-center">
                                    <MailIcon className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                                    <p className="text-[12px] text-muted-foreground mb-1">Email</p>
                                    <p className="text-[18px] font-medium break-words overflow-hidden">{data?.customerEmail || "-"}</p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default OrderDetails;